import { useState } from 'react';
import { Move, GymnastFriend, UserProgress } from '../../types';
import GymnastAvatar from '../GymnastAvatar';
import { Star, CheckCircle2, ShieldAlert, Sparkles, Trophy, RotateCcw } from 'lucide-react';
import { fireStepConfetti, fireMasteryConfetti } from '../../utils/confetti';

interface VariantProps {
  moves: Move[];
  friends: GymnastFriend[];
  progress: UserProgress;
  onToggleStep: (stepId: string, move: Move) => { nowCompleted: boolean; moveJustMastered: boolean };
  onSelectFriend: (friendId: string) => void;
  onResetProgress: () => void;
}

export default function VariantC({
  moves,
  friends,
  progress,
  onToggleStep,
  onSelectFriend,
  onResetProgress,
}: VariantProps) {
  const [selectedMoveId, setSelectedMoveId] = useState<string>(moves[0]?.id || 'move-backbend');
  const activeMove = moves.find(m => m.id === selectedMoveId) || moves[0];
  const activeFriend = friends.find(f => f.id === progress.activeFriendId) || friends[0];

  const handleToggle = (stepId: string) => {
    const res = onToggleStep(stepId, activeMove);
    if (res.moveJustMastered) {
      fireMasteryConfetti();
    } else if (res.nowCompleted) {
      fireStepConfetti();
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 pb-24">
      {/* Studio Header */}
      <header className="flex items-center justify-between bg-white rounded-3xl p-6 shadow-md border-2 border-purple-200 mb-6">
        <div className="flex items-center gap-3">
          <div className="text-3xl">🏆</div>
          <div>
            <h1 className="text-2xl font-black text-purple-700">Gymnast Team Studio</h1>
            <p className="text-xs md:text-sm font-semibold text-slate-600">
              Pick your teammate buddy & work through the checklist!
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-yellow-100 border-2 border-yellow-300 px-4 py-2 rounded-full">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-400 animate-bounce" />
            <span className="text-xl font-black text-yellow-800">{progress.earnedStars}</span>
            <span className="text-xs font-bold text-yellow-700">Stars</span>
          </div>

          <button
            onClick={onResetProgress}
            title="Reset progress"
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Main Split Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column: Team Locker */}
        <aside aria-label="Team locker" className="space-y-4">
          <div className="bg-white rounded-3xl p-5 shadow-lg border-2 border-purple-200">
            <h2 className="text-xs uppercase font-extrabold text-purple-600 tracking-wider mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span>Choose Your Buddy</span>
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-1 gap-2.5">
              {friends.map(f => {
                const isSelected = f.id === activeFriend.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => onSelectFriend(f.id)}
                    className={`flex items-center gap-3 p-2.5 rounded-2xl border text-left transition-all ${
                      isSelected
                        ? 'bg-purple-100 border-purple-400 ring-2 ring-purple-300 scale-[1.02]'
                        : 'bg-slate-50 border-slate-200 hover:bg-white'
                    }`}
                  >
                    <GymnastAvatar friend={f} size="sm" pose={isSelected ? 'cheer' : 'ready'} />
                    <div className="min-w-0">
                      <div className="font-extrabold text-sm text-slate-800 truncate">{f.name}</div>
                      <div className="text-[11px] font-semibold text-purple-600 truncate">{f.favoriteMove} Pro</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Coach Speech Box */}
          <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-3xl p-5 border-2 border-pink-300 shadow">
            <div className="flex items-center gap-2 text-xs font-black uppercase text-pink-700 mb-1">
              <span>{activeFriend.badgeEmoji}</span>
              <span>{activeFriend.name}'s Coaching Tip</span>
            </div>
            <p className="text-xs font-bold text-slate-700 italic">
              "{activeFriend.cheers[0]}"
            </p>
          </div>
        </aside>

        {/* Right Column: Move Checklist (2 cols) */}
        <main className="md:col-span-2 space-y-4">
          {/* Move Selector */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {moves.map(m => (
              <button
                key={m.id}
                onClick={() => setSelectedMoveId(m.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm transition-all whitespace-nowrap ${
                  m.id === activeMove.id
                    ? 'bg-purple-600 text-white shadow-md scale-105'
                    : 'bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span>{m.badgeEmoji}</span>
                <span>{m.name}</span>
              </button>
            ))}
          </div>

          {/* Checklist Card */}
          <div className="bg-white rounded-3xl p-6 shadow-xl border-4 border-purple-200">
            <div className="flex items-center justify-between border-b border-purple-100 pb-4 mb-4">
              <div>
                <h3 className="text-xl font-black text-slate-800">{activeMove.name} Training Checklist</h3>
                <p className="text-xs font-semibold text-purple-600">{activeMove.subtitle}</p>
              </div>

              {progress.masteredMoveIds[activeMove.id] && (
                <div className="flex items-center gap-1.5 bg-yellow-100 text-yellow-800 font-extrabold text-xs px-3 py-1.5 rounded-full border border-yellow-300">
                  <Trophy className="w-4 h-4 text-yellow-600" />
                  <span>MASTERED!</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {activeMove.steps.map(step => {
                const isComplete = !!progress.completedStepIds[step.id];
                return (
                  <div
                    key={step.id}
                    className={`p-4 rounded-2xl border-2 transition-all ${
                      isComplete
                        ? 'bg-emerald-50/70 border-emerald-300 shadow-sm'
                        : 'bg-slate-50/60 border-slate-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-purple-200 text-purple-800 font-black text-xs flex items-center justify-center">
                          {step.number}
                        </span>
                        <h4 className="font-bold text-slate-800 text-sm md:text-base">
                          {step.title}
                        </h4>
                      </div>

                      <button
                        onClick={() => handleToggle(step.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs transition-all ${
                          isComplete
                            ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                            : 'bg-white text-slate-600 border border-slate-300 hover:bg-purple-50 hover:text-purple-700'
                        }`}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{isComplete ? 'Done! ⭐' : 'Check off'}</span>
                      </button>
                    </div>

                    <p className="text-xs md:text-sm text-slate-600 font-medium mb-2 pl-8">
                      {step.description}
                    </p>

                    {step.safetyTip && (
                      <div className="ml-8 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 p-2 rounded-xl border border-amber-200">
                        <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{step.safetyTip}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
